import { addNodeToBranch, updateNodeInBranch, addBranch } from "../../redux/Slice/Workflow/workflowSlice";

export const CONTACT_FIELDS_BRANCH_ACTION_NAME = "Branch: Contact Fields";

// Built-in contact fields (stored on the contact itself, not as profile-field definitions).
// Ids are matched by the server's contactFieldsCondition.service.js.
export const CORE_CONTACT_FIELD_OPTIONS = [
  { _id: "core:name", name: "Name" },
  { _id: "core:phone", name: "Phone number" },
  { _id: "core:email", name: "Email" },
  { _id: "core:businessName", name: "Business name" },
];

export const DEFAULT_CONTACT_FIELDS_VALUE = { fieldIds: [], matchMode: "all" };

// Same definition the server seeds for WhatsApp (workflow.actionDefaults.js); Instagram, Facebook
// and Website drawers keep their action lists locally, so they reuse this one.
export const CONTACT_FIELDS_BRANCH_ACTION = {
  name: CONTACT_FIELDS_BRANCH_ACTION_NAME,
  description: "Splits the workflow into a YES path (the contact has a value for the selected profile fields) and a NO path (it doesn't).",
  category: "Logic",
  config: [
    {
      name: "Contact Fields Node",
      required: true,
      fields: [{ label: "Configuration", type: "contactFieldsCondition", selectedValue: { fieldIds: [], matchMode: "all" } }],
    },
  ],
};

export const isContactFieldsBranchAction = (name) => name === CONTACT_FIELDS_BRANCH_ACTION_NAME;

export const isContactFieldsValueValid = (value) => Array.isArray(value?.fieldIds) && value.fieldIds.length > 0;

export const buildContactFieldsConfig = (configValue) => [
  {
    name: "Contact Fields Node",
    required: true,
    fields: [{ label: "Configuration", type: "contactFieldsCondition", selectedValue: configValue }],
  },
];

/**
 * Creates (or reuses, when editing) the two label-only yes/no branches and stamps
 * next/branches/condition onto `actionWithConfig`. Same mechanism as
 * applyBusinessHoursBranches: the executor evaluates the condition synchronously and jumps
 * straight to one of the branches, so there is no reply to match.
 */
export const applyContactFieldsBranches = async ({ dispatch, editAction, actionWithConfig, configValue }) => {
  const currentState = window.store?.getState ? window.store.getState() : null;
  const currentBranches = currentState?.workflow?.selectedWorkflow?.currentVersion?.branches || {};

  // Reuse the existing branch key when editing so downstream nodes aren't orphaned.
  const resolveBranchId = (branchName) => editAction?.branches?.[branchName] || `option-${branchName}-${Date.now()}`;
  const yesBranchId = resolveBranchId("yes");
  const noBranchId = resolveBranchId("no");

  for (const [branchName, branchId, label] of [
    ["yes", yesBranchId, "Contact fields: Has value"],
    ["no", noBranchId, "Contact fields: No value"],
  ]) {
    if (!currentBranches[branchId]) {
      await dispatch(addBranch({ branchId }));
    }
    const triggerNode = {
      id: `${branchId}-trigger`,
      type: "trigger",
      trigger: {
        name: label,
        description: branchName === "yes"
          ? "Runs when the contact has a value for the selected profile fields."
          : "Runs when the contact is missing a value for the selected profile fields.",
        triggerType: "non-root",
        config: [],
        category: "Nil",
      },
    };
    await dispatch(updateNodeInBranch({ branchId, nodeId: `${branchId}-trigger`, updates: triggerNode }));
    const stateAfterUpdate = window.store?.getState ? window.store.getState() : null;
    const branchAfterUpdate = stateAfterUpdate?.workflow?.selectedWorkflow?.currentVersion?.branches?.[branchId];
    if (!branchAfterUpdate || branchAfterUpdate.length === 0) {
      await dispatch(addNodeToBranch({ branchId, node: triggerNode }));
    }
  }

  actionWithConfig.next = [yesBranchId, noBranchId];
  actionWithConfig.branches = { yes: yesBranchId, no: noBranchId };
  actionWithConfig.condition = {
    type: "contact_fields",
    fieldIds: configValue?.fieldIds || [],
    matchMode: configValue?.matchMode === "any" ? "any" : "all",
  };
};
